import React from "react";
import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { FaFlask, FaArrowLeft, FaPaintBrush } from "react-icons/fa";

function Icon(component: any, className?: string) {
  return React.createElement(
    component as React.ComponentType<{ className?: string }>,
    { className }
  );
}

/* ─── Data ──────────────────────────────────────────────── */

/* ─── Animation Variants ────────────────────────────────── */

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

/* ─── Component ─────────────────────────────────────────── */

const Labs: React.FC = () => {
  return (
    <div>
      {/* ── Hero ── */}
      <section className="pt-24 pb-10 px-4 sm:px-6 relative overflow-hidden">
        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(var(--color-accent) 1px, transparent 1px), linear-gradient(90deg, var(--color-accent) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Back link */}
            <RouterLink
              to="/"
              className="inline-flex items-center gap-1.5 text-xs text-textSecondary/60 hover:text-secondary transition-colors mb-6"
            >
              {Icon(FaArrowLeft, "text-[10px]")}
              Back to Home
            </RouterLink>

            {/* Header */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border/50 mb-5">
              {Icon(FaFlask, "text-xs text-secondary")}
              <span className="font-mono text-[11px] text-textSecondary/60 tracking-wider uppercase">
                Labs
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-textPrimary mb-3">
              Creative{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
                Labs
              </span>
            </h1>
            <p className="text-textSecondary text-sm max-w-lg">
              A space for UI/UX prototypes, design explorations, and creative coding experiments
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── UI/UX Prototypes ── */}
      <section className="py-10 px-4 sm:px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="mb-8">
              <h2 className="text-xl font-bold text-textPrimary mb-2">
                UI/UX{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
                  Prototypes
                </span>
              </h2>
              <p className="text-xs text-textSecondary">
                Design concepts and interactive prototypes
              </p>
            </motion.div>

            {/* Tag filter */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-2 mb-8">
              {["all", "figma", "ui design", "landing"].map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold capitalize bg-tertiary/50 border border-border/50 text-textSecondary/50 cursor-default"
                >
                  {tag}
                </span>
              ))}
            </motion.div>

            {/* Empty state */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col items-center justify-center py-20 rounded-xl border border-border/30 bg-white/50 dark:bg-[#161b22]/50"
            >
              {Icon(FaPaintBrush, "text-4xl text-textSecondary/30 mb-4")}
              <p className="text-sm text-textSecondary mb-1">
                No UI/UX designs found.
              </p>
              <p className="text-xs text-textSecondary/50">Coming soon.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Labs;
